import { z } from 'zod';

import { defineFcm } from '../shared/factory';

const DEFAULT_MILESTONES: readonly number[] = [25];
const DEFAULT_MAX_OVERSHOOT_MULTIPLIER = 2;

/** Valid milestones, sorted and de-duplicated; falls back to the default if none remain. */
function normalizeMilestones(value: unknown): number[] {
	const valid = Array.isArray(value)
		? value.filter((milestone): milestone is number => Number.isInteger(milestone) && milestone > 0)
		: [];
	const milestones = [...new Set(valid)].sort((a, b) => a - b);

	return milestones.length ? milestones : [...DEFAULT_MILESTONES];
}

export const FCM_STARTER_EXPERIENCE_DASHBOARD_UPDATE_NUDGES = defineFcm('starter_experience_dashboard_update_nudges')
	.asJson(
		z.object({
			enabled: z.boolean().catch(false),
			milestones: z.unknown().optional().transform(normalizeMilestones),
			maxOvershootMultiplier: z.number().gt(1).catch(DEFAULT_MAX_OVERSHOOT_MULTIPLIER),
		}),
	)
	.withDefault({
		enabled: false,
		milestones: [...DEFAULT_MILESTONES],
		maxOvershootMultiplier: DEFAULT_MAX_OVERSHOOT_MULTIPLIER,
	})
	// A level without a row is passed in as the default value, so country tuning would always be
	// overwritten by the locale's: tuning is read from the locale row only.
	.withMerger(({ countryValue, localeValue }) => ({
		...localeValue,
		enabled: countryValue.enabled && localeValue.enabled,
	}))
	.toConfig({
		owner: 'squad-axolotl',
		scope: 'corgi-front-end',
		context: 'Starter Dashboard',
		description: `Controls the update nudges on the starter dashboard: cards that prompt a petition starter to post an update when their petition reaches a signature milestone.

			Fields:
			• enabled: turns the nudges on. Off by default.
			• milestones: signature counts that trigger a nudge, as positive integers (sorted and deduplicated on read; invalid entries are dropped, and the default is used if none are left).
			• maxOvershootMultiplier: a milestone nudge is only shown while the petition's signature count is below milestone * this value, so a petition that long passed a milestone is not told it just hit it. Must be more than 1.

			Invalid values fall back to their default.`,
		mergeLogic:
			'Checks both country and locale; both must be enabled for the nudges to be enabled. The other fields are read from the locale row only; country rows only contribute `enabled`.',
		supportedGroups: { countries: true, locales: true },
		template: {
			enabled: false,
			milestones: [...DEFAULT_MILESTONES],
			maxOvershootMultiplier: DEFAULT_MAX_OVERSHOOT_MULTIPLIER,
		},
	});
